import { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '../components/ui/button';
import { Send, Loader2, RotateCcw, Copy, Check, Bot, User, Sparkles, ChevronDown } from 'lucide-react';
import { demoProjects, demoPeople } from '../data/dummyData';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STARTER_PROMPTS = [
  "Generate a budget for my indie thriller shooting in Mumbai over 30 days",
  "Find me a cinematographer with low-light experience for a gritty film",
  "Create a 60-day production timeline for a feature film",
  "Draft a contract outline for a cinematographer engagement",
  "Suggest the ideal crew composition for a music video shoot",
  "What tasks should I prioritize for pre-production?",
];

export default function SpecterPage() {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [sessionId] = useState(() => `spectre_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`);
  const [copiedIdx, setCopiedIdx] = useState(null);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]); // eslint-disable-line react-hooks/exhaustive-deps -- scrollToBottom is stable (refs don't change)

  useEffect(() => {
    inputRef.current?.focus();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps -- run once on mount

  const handleCopy = (text, idx) => {
    navigator.clipboard.writeText(text);
    setCopiedIdx(idx);
    setTimeout(() => setCopiedIdx(null), 2000);
  };

  const sendMessage = async (text) => {
    const userMsg = text || input.trim();
    if (!userMsg || loading) return;
    setInput('');

    const newMessages = [...messages, { role: 'user', content: userMsg }];
    setMessages(newMessages);
    setLoading(true);

    try {
      const res = await fetch(`${API}/spectre/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify({
          message: userMsg,
          session_id: sessionId,
          context: {
            projects: demoProjects.map(p => ({ name: p.name, type: p.project_type, status: p.status, budget: p.budget })),
            people_count: demoPeople.length,
          }
        }),
      });
      const data = await res.json();
      setMessages(prev => [...prev, { role: 'assistant', content: data.response || 'I encountered an issue. Please try again.' }]);
    } catch {
      setMessages(prev => [...prev, { role: 'assistant', content: 'Connection failed. Please check your network and try again.' }]);
    } finally {
      setLoading(false);
      inputRef.current?.focus();
    }
  };

  const handleReset = () => {
    setMessages([]);
    setInput('');
    inputRef.current?.focus();
  };

  const formatMessage = (content) => {
    if (typeof content !== 'string') {
      return JSON.stringify(content, null, 2);
    }
    return content;
  };

  const isEmpty = messages.length === 0;

  return (
    <div className="flex flex-col h-[calc(100vh-48px)] -m-6 lg:-m-8" data-testid="spectre-page">
      {isEmpty ? (
        <div className="flex-1 flex flex-col items-center justify-center px-6">
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center max-w-2xl"
          >
            <div className="w-10 h-10 rounded-xl bg-foreground flex items-center justify-center mx-auto mb-6">
              <Bot className="w-5 h-5 text-background" strokeWidth={2} />
            </div>
            <h1 className="font-heading text-2xl sm:text-3xl font-medium tracking-tight mb-2" data-testid="spectre-title">
              Specter
            </h1>
            <p className="text-sm text-muted-foreground mb-10 max-w-md mx-auto">
              Your AI production assistant. Generate budgets, find collaborators, plan timelines, draft contracts, and more.
            </p>

            <div className="grid sm:grid-cols-2 gap-2 max-w-xl mx-auto">
              {STARTER_PROMPTS.map((prompt) => (
                <button
                  key={prompt}
                  onClick={() => sendMessage(prompt)}
                  className="text-left p-3.5 rounded-xl border border-border/60 bg-card hover:bg-secondary/50 hover:border-border transition-all duration-200 group"
                  data-testid={`starter-prompt-${prompt.slice(0, 20).replace(/\s/g, '-')}`}
                >
                  <p className="text-xs text-muted-foreground group-hover:text-foreground transition-colors leading-relaxed">
                    {prompt}
                  </p>
                </button>
              ))}
            </div>
          </motion.div>
        </div>
      ) : (
        <div className="flex-1 overflow-y-auto">
          <div className="max-w-3xl mx-auto px-6 py-6 space-y-1">
            <AnimatePresence initial={false}>
              {messages.map((msg, i) => (
                <motion.div
                  key={`msg-${msg.role}-${i}`}
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="py-4"
                >
                  <div className="flex gap-3">
                    <div className={`w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5 ${
                      msg.role === 'assistant' ? 'bg-foreground' : 'bg-secondary'
                    }`}>
                      {msg.role === 'assistant'
                        ? <Bot className="w-3.5 h-3.5 text-background" strokeWidth={2} />
                        : <User className="w-3.5 h-3.5 text-foreground" strokeWidth={2} />
                      }
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-[11px] font-medium text-muted-foreground mb-1.5">
                        {msg.role === 'assistant' ? 'Specter' : 'You'}
                      </p>
                      <div className="text-sm leading-relaxed text-foreground whitespace-pre-wrap break-words" data-testid={`message-${i}`}>
                        {formatMessage(msg.content)}
                      </div>
                      {msg.role === 'assistant' && (
                        <div className="mt-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-6 px-2 text-[10px] text-muted-foreground hover:text-foreground"
                            onClick={() => handleCopy(formatMessage(msg.content), i)}
                            data-testid={`copy-msg-${i}`}
                          >
                            {copiedIdx === i ? <Check className="w-3 h-3 mr-1" /> : <Copy className="w-3 h-3 mr-1" />}
                            {copiedIdx === i ? 'Copied' : 'Copy'}
                          </Button>
                        </div>
                      )}
                    </div>
                  </div>
                </motion.div>
              ))}
            </AnimatePresence>

            {loading && (
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="py-4">
                <div className="flex gap-3">
                  <div className="w-7 h-7 rounded-lg bg-foreground flex items-center justify-center flex-shrink-0 mt-0.5">
                    <Bot className="w-3.5 h-3.5 text-background" strokeWidth={2} />
                  </div>
                  <div className="flex items-center gap-1.5 pt-2">
                    <div className="w-1.5 h-1.5 rounded-full bg-muted-foreground/40 animate-pulse" style={{ animationDelay: '0ms' }} />
                    <div className="w-1.5 h-1.5 rounded-full bg-muted-foreground/40 animate-pulse" style={{ animationDelay: '150ms' }} />
                    <div className="w-1.5 h-1.5 rounded-full bg-muted-foreground/40 animate-pulse" style={{ animationDelay: '300ms' }} />
                  </div>
                </div>
              </motion.div>
            )}
            <div ref={messagesEndRef} />
          </div>
        </div>
      )}

      {/* Input area */}
      <div className="border-t border-border bg-background px-6 py-4">
        <div className="max-w-3xl mx-auto">
          <div className="flex items-end gap-2">
            {messages.length > 0 && (
              <Button
                variant="ghost"
                size="icon"
                className="h-10 w-10 flex-shrink-0 text-muted-foreground hover:text-foreground"
                onClick={handleReset}
                data-testid="reset-chat-btn"
              >
                <RotateCcw className="w-4 h-4" strokeWidth={2} />
              </Button>
            )}
            <div className="flex-1 relative">
              <textarea
                ref={inputRef}
                value={input}
                onChange={e => setInput(e.target.value)}
                onKeyDown={e => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    sendMessage();
                  }
                }}
                placeholder="Ask Specter anything..."
                rows={1}
                className="w-full resize-none rounded-xl border border-border bg-card px-4 py-2.5 pr-12 text-sm placeholder:text-muted-foreground/50 focus:outline-none focus:ring-1 focus:ring-ring min-h-[42px] max-h-[120px]"
                style={{ height: 'auto', overflow: 'hidden' }}
                onInput={e => {
                  e.target.style.height = 'auto';
                  e.target.style.height = Math.min(e.target.scrollHeight, 120) + 'px';
                }}
                data-testid="spectre-input"
              />
              <Button
                size="icon"
                className="absolute right-1.5 bottom-1.5 h-7 w-7 rounded-lg"
                onClick={() => sendMessage()}
                disabled={!input.trim() || loading}
                data-testid="spectre-send-btn"
              >
                {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
              </Button>
            </div>
          </div>
          <p className="text-[10px] text-muted-foreground/50 mt-2 text-center">
            Specter can make mistakes. Verify important information.
          </p>
        </div>
      </div>
    </div>
  );
}
